import { useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowUp, Check, Lock, Smile, TriangleAlert } from 'lucide-react';
import {
  useCaseDetail, useCaseEventCreate, useCaseNudge, useCaseTransition,
  useStudentAcademic, useStudentOverview, useStudentParent,
} from '../api/hooks';
import {
  Badge, Button, EmptyState, ErrorPanel, PagedFooter, PanelCard, PillTabs, Segmented, Tabs, TextLink, Track,
  TimelineRail, type TimelineEntry,
} from '../components/ui';
import CaseEventBody, { caseEventIcon } from '../components/ui/CaseEventBody';
import { FaClipboardList } from '../components/ui/icons';
import { relativeTime } from '../components/studentRow';
import { caseStatusLabel, caseStatusTone, prettifySlug } from '../lib/caseStatus';
import { isKnownEventKind, timelineTitle } from '../lib/caseEvents';
import { averageDelta4w, deltaLabel, subjectAverages } from '../lib/caseSnapshot';
import { dayMonth, dayMonthTime } from '../lib/dates';
import { nameOr } from '../lib/names';
import usePagination from '../lib/usePagination';
import { LINK_BTN } from '../components/ui/linkButton';
import type { CaseEvent, CaseLinkedAlert } from '../api/types';
import { gradeSectionLabel } from './dashboards/sections/format';

const DAY = 86_400_000;

type Composer = 'note' | 'nudge' | 'contact';
type Filter = 'all' | 'notes' | 'contacts' | 'actions';
type MoveTo = 'open' | 'in_progress' | 'escalated' | 'resolved';

const MOVE_OPTIONS: { key: MoveTo; label: string; inert?: boolean }[] = [
  { key: 'open', label: 'Open', inert: true }, // the backend rejects "open"; it only shows the current state
  { key: 'in_progress', label: 'In progress' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'resolved', label: 'Resolved' },
];
const OUTCOMES = [
  { key: 'improved', label: 'Improved' },
  { key: 'no_change', label: 'No change' },
  { key: 'worsened', label: 'Worsened' },
  { key: 'transferred', label: 'Transferred' },
  { key: 'na', label: 'N/A' },
];
const CHANNELS = [
  { key: 'phone', label: 'Phone call' },
  { key: 'telegram', label: 'Telegram' },
  { key: 'email', label: 'Email' },
  { key: 'in_person', label: 'In person' },
];

const FIELD = 'w-full rounded-[12px] border border-line-strong bg-surface px-3.5 py-3 text-sm text-ink outline-none placeholder:text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-accent';
const LABEL = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const SL = 'font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';

function Fact({ label, sub, children }: { label: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</dt>
      <dd className="mt-1 flex items-center gap-2 font-semibold text-ink">{children}</dd>
      {sub && <p className="text-xs text-muted">{sub}</p>}
    </div>
  );
}

/** "Critical" / "Warning" pill and tile for a linked alert (design `.ini` tint + `.pill`). */
const SEVERITY = {
  critical: { tone: 'danger' as const, tile: 'bg-danger-bg text-danger-ink', label: 'Critical', Icon: TriangleAlert },
  warning: { tone: 'warning' as const, tile: 'bg-warning-bg text-warning-ink', label: 'Warning', Icon: Smile },
};

function LinkedAlerts({ alerts }: { alerts: CaseLinkedAlert[] }) {
  if (alerts.length === 0) return null;
  return (
    <PanelCard title="Linked alerts" actions={<TextLink to="/alerts">Alerts →</TextLink>}>
      <ul>
        {alerts.map((a) => {
          const sev = a.severity === 'critical' ? SEVERITY.critical : a.severity === 'warning' ? SEVERITY.warning : null;
          const Icon = sev?.Icon ?? Smile;
          return (
            <li key={a.id} className="border-b border-hairline last:border-b-0">
              <Link to="/alerts" className="flex items-center gap-3 py-[11px] text-ink">
                <span aria-hidden="true" className={`grid h-9 w-9 flex-none place-items-center rounded-[12px] ${sev?.tile ?? 'bg-track text-muted-2'}`}><Icon size={18} strokeWidth={1.8} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{a.rule_name}</span>
                  <span className="text-xs text-muted">Fired {dayMonth(a.fired_at)}</span>
                </span>
                <Badge tone={sev?.tone ?? 'neutral'}>{sev?.label ?? prettifySlug(a.severity)}</Badge>
              </Link>
            </li>
          );
        })}
      </ul>
    </PanelCard>
  );
}

function StudentSnapshot({ studentId, events }: { studentId: number; events: CaseEvent[] }) {
  const ov = useStudentOverview(studentId);
  const parent = useStudentParent(studentId);
  const academic = useStudentAcademic(studentId);
  // Roles that may not read the student's profile get a 403: show nothing, no error noise.
  if (ov.isError || !ov.data) return null;
  const o = ov.data;
  const p = parent.data;
  const results = academic.data?.results ?? [];
  // Value and change come from the same results; without results the overview average stands alone.
  const scored = results.filter((r) => r.score_percentage != null).map((r) => r.score_percentage as number);
  const average = scored.length > 0 ? scored.reduce((a, b) => a + b, 0) / scored.length : o.kpis.avg_score;
  const delta = averageDelta4w(results);
  const subjects = subjectAverages(results).slice(0, 3);
  const lastActive = o.kpis.last_active_at;
  const lastNudge = events.filter((e) => e.kind === 'nudge_sent').map((e) => new Date(e.created_at).getTime()).sort((a, b) => b - a)[0];
  const backAfterNudge = !!lastActive && lastNudge != null && new Date(lastActive).getTime() > lastNudge;
  const parentBits = p && p.parent_name
    ? [`Parent: ${p.parent_name}`, `digest consent ${p.parent_digest_consent ? 'on' : 'off'}`, `Telegram ${p.telegram_linked ? 'linked' : 'not linked'}`]
    : [];
  return (
    <PanelCard title="Student snapshot" actions={<TextLink to={`/students/profile/${studentId}`}>Student 360 →</TextLink>}>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="rounded-[14px] bg-ground p-3">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Average</span>
          <div className="mt-1 font-mono text-[22px] font-semibold">{average != null ? `${Math.round(average * 10) / 10}%` : '—'}</div>
          {delta != null && <span className={`font-mono text-xs ${delta < 0 ? 'text-danger' : 'text-success'}`}>{deltaLabel(delta)}</span>}
        </div>
        <div className="rounded-[14px] bg-ground p-3">
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Last active</span>
          <div className="mt-1 font-mono text-[22px] font-semibold">{relativeTime(lastActive)}</div>
          {backAfterNudge && <span className="font-mono text-xs text-success">back after nudge</span>}
        </div>
      </div>
      {subjects.length > 0 && (
        <div className="mt-3.5 flex flex-col gap-3">
          {subjects.map((s) => (
            <div key={s.subject}>
              <div className="mb-1.5 flex justify-between text-[13px]"><span>{s.subject}</span><span className="font-mono">{s.pct}%</span></div>
              <Track value={s.pct} tone={s.pct < 50 ? 'accent' : 'slate'} label={`${s.subject} average`} />
            </div>
          ))}
        </div>
      )}
      <dl className="mt-3.5 space-y-2 text-[13px]">
        {o.risk && <Row k="Risk" v={prettifySlug(o.risk.band)} />}
        <Row k="Open alerts" v={String(o.open_alerts.length)} />
        {p && p.parent_email && <Row k="Parent email" v={p.parent_email} />}
      </dl>
      {parentBits.length > 0 && <p className="mt-3.5 text-[13px] text-muted">{parentBits.join(' · ')}</p>}
    </PanelCard>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted">{k}</dt>
      <dd className="min-w-0 break-words text-right font-semibold text-ink">{v}</dd>
    </div>
  );
}

export default function CaseDetail() {
  const { caseId } = useParams();
  const id = Number(caseId);
  const q = useCaseDetail(id);
  const ev = useCaseEventCreate();
  const nudge = useCaseNudge();
  const trans = useCaseTransition();
  const [composer, setComposer] = useState<Composer>('note');
  const [note, setNote] = useState('');
  const [nudgeTitle, setNudgeTitle] = useState('Keep going!');
  const [nudgeBody, setNudgeBody] = useState('');
  const [contactBody, setContactBody] = useState('');
  const [channel, setChannel] = useState('phone');
  const [showResolve, setShowResolve] = useState(false);
  const [outcome, setOutcome] = useState('improved');
  const [outcomeNote, setOutcomeNote] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const events = q.data?.events;
  const caseRow = q.data?.case;
  const linked = q.data?.linked_alerts;
  // Only alerts fired at or before the case was opened can be why it was opened (later ones were linked in afterwards).
  const openingAlerts = useMemo(() => {
    const opened = caseRow ? new Date(caseRow.opened_at).getTime() : 0;
    return (linked ?? []).filter((a) => new Date(a.fired_at).getTime() <= opened + 60_000);
  }, [linked, caseRow]);
  // "Case opened" is shown as the first timeline entry, from the case's own opened_at.
  const sorted = useMemo(() => {
    const all: CaseEvent[] = [...(events ?? [])];
    if (caseRow) {
      all.push({
        id: -caseRow.id, case: caseRow.id, kind: 'case_opened',
        body: openingAlerts.length > 0 ? `Alerts: ${Array.from(new Set(openingAlerts.map((a) => a.rule_name))).join(', ')}` : '',
        payload: {}, actor_username: caseRow.opened_by_username, actor_name: caseRow.opened_by_name ?? null, created_at: caseRow.opened_at,
      });
    }
    return all.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  }, [events, caseRow, openingAlerts]);

  const actionKind = (kind: string): Filter => (kind === 'note' || kind === 'case_opened' ? 'notes' : kind === 'parent_contacted' ? 'contacts' : 'actions');
  const count = (f: Filter) => (f === 'all' ? sorted.length : sorted.filter((e) => actionKind(e.kind) === f).length);
  const shown = filter === 'all' ? sorted : sorted.filter((e) => actionKind(e.kind) === filter);
  const pg = usePagination(shown, 10, filter);
  const section = useStudentOverview(q.data?.case.student ?? 0).data?.section ?? null;

  if (q.isLoading) {
    return (
      <div role="status" aria-label="Loading case" className="space-y-4">
        <div className="h-40 animate-pulse rounded-[20px] bg-track" />
        <div className="h-56 animate-pulse rounded-[20px] bg-track" />
        <div className="h-72 animate-pulse rounded-[20px] bg-track" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    const status = (q.error as { response?: { status?: number } } | null)?.response?.status;
    if (status === 404) {
      return (
        <PanelCard>
          <EmptyState icon={<FaClipboardList />} title="Case not found"
            body={<Link to="/cases" className="font-semibold text-ink underline">Back to Cases</Link>} />
        </PanelCard>
      );
    }
    return <PanelCard><ErrorPanel body="Could not load this case. Try again." onRetry={() => q.refetch()} /></PanelCard>;
  }
  const c = q.data.case;
  const busy = ev.isPending || nudge.isPending || trans.isPending;

  function addNote() {
    if (!note.trim()) return;
    ev.mutate({ caseId: id, body: { kind: 'note', body: note } }, {
      onSuccess: () => { toast.success('Note added'); setNote(''); },
      onError: () => toast.error('Failed'),
    });
  }
  function sendNudge() {
    if (!nudgeBody.trim()) return;
    nudge.mutate({ caseId: id, body: { title: nudgeTitle, body: nudgeBody } }, {
      onSuccess: () => { toast.success('Nudge sent'); setNudgeBody(''); },
      onError: () => toast.error('Failed'),
    });
  }
  function logContact() {
    if (!contactBody.trim()) return;
    ev.mutate({ caseId: id, body: { kind: 'parent_contacted', body: contactBody, payload: { channel } } }, {
      onSuccess: () => { toast.success('Contact logged'); setContactBody(''); },
      onError: () => toast.error('Failed'),
    });
  }
  function escalate(kind: 'escalated_to_counselor' | 'escalated_to_admin') {
    ev.mutate({ caseId: id, body: { kind, body: 'Escalation from panel' } }, {
      onSuccess: () => toast.success('Escalated'),
      onError: () => toast.error('Failed'),
    });
  }
  function transition(to: string, extra: Record<string, string> = {}, onDone?: () => void) {
    trans.mutate({ id, body: { to, ...extra } }, {
      onSuccess: () => { toast.success(`Moved to ${caseStatusLabel(to)}`); onDone?.(); },
      onError: () => toast.error('Failed'),
    });
  }
  function moveTo(k: MoveTo) {
    if (k === 'open' || busy || (k === c.status && !showResolve)) return; // the backend rejects "open"; it only shows the current state
    if (k === 'resolved') { setShowResolve(true); return; }
    transition(k);
  }

  const alerts = linked ?? [];
  const reason = Array.from(new Set(alerts.map((a) => a.rule_name))).join(', ');
  const entries: TimelineEntry[] = pg.pageItems.map((e) => {
    const meta = caseEventIcon(e.kind);
    return {
      id: e.id,
      timestamp: e.created_at,
      actor: nameOr(e.actor_name, e.actor_username, ''),
      icon: meta.icon,
      tone: meta.tone,
      title: e.kind === 'case_opened' ? (openingAlerts.length > 0 ? 'Case opened from alert' : 'Case opened')
        : isKnownEventKind(e.kind) ? timelineTitle(e.kind, e.payload) : undefined,
      body: <CaseEventBody event={e} hideTitle />,
    };
  });

  const end = c.closed_at ? new Date(c.closed_at).getTime() : Date.now();
  const daysOpen = Math.max(0, Math.floor((end - new Date(c.opened_at).getTime()) / DAY));
  const lastEvent = (events ?? []).map((e) => e.created_at).sort().reverse()[0] ?? null;
  const resolved = c.status === 'resolved';
  const firstName = c.student_name.split(' ')[0];
  const classLine = c.student_grade || c.student_section
    ? gradeSectionLabel(c.student_grade, c.student_section, false)
    : section?.display ?? '';
  const subtitle = [classLine, reason].filter(Boolean).join(' · ');
  const initials = c.student_name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const counselorOnly = c.visibility === 'counselor_admin_only';
  const visibilityLabel = counselorOnly ? 'Counselor & admin only' : c.visibility === 'teachers_of_student' ? 'Teachers of student' : prettifySlug(c.visibility) || '—';
  // A note is as visible as its case.
  const noteAudience = counselorOnly ? 'Visible to the counselor and admins only.'
    : c.visibility === 'teachers_of_student' ? 'Visible to teachers of this student, the counselor and admins.' : null;

  return (
    <div>
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-3">
        <nav aria-label="Breadcrumb">
          <ol className="flex flex-wrap items-center gap-2 text-[13px] text-muted">
            <li className="flex items-center gap-2"><Link className="font-semibold text-muted-2 hover:underline" to="/cases">Cases</Link><span aria-hidden>/</span></li>
            <li aria-current="page">Case #{c.id}</li>
          </ol>
        </nav>
        <Link to="/cases" className={LINK_BTN}><ArrowLeft size={18} strokeWidth={1.8} aria-hidden /> All cases</Link>
      </div>

      <PanelCard className="mb-[18px]">
        <div className="flex flex-wrap items-center gap-5">
          <span aria-hidden="true" className="grid h-16 w-16 flex-none place-items-center rounded-[20px] bg-mist font-display text-[22px] font-bold text-ink">{initials}</span>
          <div className="min-w-0 flex-[1_1_300px]">
            <p className="mb-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
              Case #{c.id} · {prettifySlug(c.category)}
            </p>
            <h1 className="break-words font-display text-[30px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink sm:text-[34px]">
              <Link to={`/students/profile/${c.student}`} className="hover:underline">{c.student_name}</Link>
            </h1>
            {subtitle && <p className="mt-1.5 text-[15px] text-muted">{subtitle}</p>}
          </div>
          <div className="flex flex-col items-end gap-2.5">
            <Badge tone={caseStatusTone(c.status)} size="md">{caseStatusLabel(c.status)}</Badge>
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" icon={<ArrowUp size={18} strokeWidth={1.8} aria-hidden="true" />} disabled={busy} onClick={() => escalate('escalated_to_counselor')}>Escalate to counselor</Button>
              <Button variant="ghost" disabled={busy} onClick={() => escalate('escalated_to_admin')}>Escalate to admin</Button>
              {!resolved && <Button variant="primary" icon={<Check size={18} strokeWidth={1.8} aria-hidden="true" />} disabled={busy} onClick={() => setShowResolve(true)}>Resolve case</Button>}
            </div>
          </div>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-3 border-t border-hairline pt-[18px] min-[821px]:grid-cols-4">
          <Fact label="Opened" sub={`by ${nameOr(c.opened_by_name, c.opened_by_username, '—')}`}>{dayMonthTime(c.opened_at)}</Fact>
          <Fact label="Visibility">
            <Lock size={16} strokeWidth={1.8} aria-hidden="true" className="flex-none text-muted" />{visibilityLabel}
          </Fact>
          <Fact label="Last activity" sub={`${daysOpen} ${daysOpen === 1 ? 'day' : 'days'} open`}>{lastEvent ? relativeTime(lastEvent) : '—'}</Fact>
          {c.outcome && <Fact label="Outcome">{prettifySlug(c.outcome)}</Fact>}
        </dl>

        <div className="mt-[18px] flex flex-wrap items-center gap-3">
          <span className={SL}>Move to</span>
          <div className="max-w-full overflow-x-auto">
            <Segmented label="Move to" options={MOVE_OPTIONS} value={(showResolve ? 'resolved' : c.status) as MoveTo} onChange={moveTo} />
          </div>
        </div>

        {showResolve && (
          <div className="mt-4 flex flex-col gap-3 rounded-[16px] bg-ground p-4">
            <div>
              <p className={LABEL}>Outcome</p>
              <PillTabs label="Outcome" dense height={34} weight="semibold" options={OUTCOMES} value={outcome} onChange={setOutcome} />
            </div>
            <div>
              <label className={LABEL} htmlFor="outcome-note">Outcome note (optional)</label>
              <textarea id="outcome-note" rows={2} value={outcomeNote} onChange={(e) => setOutcomeNote(e.target.value)} placeholder="What changed, and what should the next teacher know?" className={FIELD} />
            </div>
            <div className="flex justify-end gap-2.5">
              <Button variant="ghost" disabled={busy} onClick={() => setShowResolve(false)}>Cancel</Button>
              <Button variant="secondary" disabled={busy} loading={trans.isPending} onClick={() => {
                transition('resolved', { outcome, outcome_note: outcomeNote }, () => {
                  setShowResolve(false); setOutcome('improved'); setOutcomeNote('');
                });
              }}>Confirm resolve</Button>
            </div>
          </div>
        )}
      </PanelCard>

      <div className="grid grid-cols-1 gap-[18px] min-[821px]:grid-cols-12">
        <div className="flex min-w-0 flex-col gap-[18px] min-[821px]:col-span-6 min-[1181px]:col-span-8">
          <PanelCard aria-label="Add to case">
            <Tabs thick label="Composer" value={composer} onChange={setComposer} items={[
              { key: 'note', label: 'Add a note' },
              { key: 'nudge', label: 'Nudge student' },
              { key: 'contact', label: 'Log parent contact' },
            ]} />
            {composer === 'nudge' && (
              <div className="mb-3">
                <label className={LABEL} htmlFor="nudge-title">Title</label>
                <input id="nudge-title" value={nudgeTitle} onChange={(e) => setNudgeTitle(e.target.value)} placeholder="Title" className={FIELD} />
              </div>
            )}
            {composer === 'note' && (
              <>
                <label className="sr-only" htmlFor="case-note">Note</label>
                <textarea id="case-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What did you observe or agree? Keep it factual." className={FIELD} />
              </>
            )}
            {composer === 'nudge' && (
              <>
                <label className="sr-only" htmlFor="nudge-body">Message</label>
                <textarea id="nudge-body" rows={3} value={nudgeBody} onChange={(e) => setNudgeBody(e.target.value)} placeholder={`Write a short, encouraging message to ${firstName}.`} className={FIELD} />
              </>
            )}
            {composer === 'contact' && (
              <>
                <label className="sr-only" htmlFor="contact-body">What was discussed</label>
                <textarea id="contact-body" rows={3} value={contactBody} onChange={(e) => setContactBody(e.target.value)} placeholder="Who you spoke with, what was agreed, any follow-up." className={FIELD} />
              </>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-3">
              {composer === 'note' && noteAudience && <span className="text-[13px] text-muted">{noteAudience}</span>}
              {composer === 'nudge' && <span className="text-[13px] text-muted">{`Sent to ${firstName}'s YADA app as a push notification.`}</span>}
              {composer === 'contact' && (
                <>
                  <span className="text-[13px] text-muted">Channel</span>
                  <Segmented dense scroll label="Channel" value={channel} onChange={setChannel} options={CHANNELS} />
                </>
              )}
              <span className="flex-1" />
              {composer === 'note' && <Button variant="secondary" disabled={busy || !note.trim()} onClick={addNote}>Add note</Button>}
              {composer === 'nudge' && <Button variant="secondary" disabled={busy || !nudgeBody.trim()} onClick={sendNudge}>Send nudge</Button>}
              {composer === 'contact' && <Button variant="secondary" disabled={busy || !contactBody.trim()} onClick={logContact}>Log contact</Button>}
            </div>
          </PanelCard>

          <PanelCard title="Timeline" subtitle={`${sorted.length} ${sorted.length === 1 ? 'event' : 'events'}, newest first`}
            actions={
              <PillTabs label="Timeline filter" height={34} weight="semibold" value={filter} onChange={setFilter} options={[
                { key: 'all', label: 'All', count: count('all') },
                { key: 'notes', label: 'Notes', count: count('notes') },
                { key: 'contacts', label: 'Contacts', count: count('contacts') },
                { key: 'actions', label: 'Actions', count: count('actions') },
              ]} />
            }>
            <TimelineRail entries={entries} />
            <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Timeline pages" className="mt-4" />
          </PanelCard>
        </div>

        <div className="flex min-w-0 flex-col gap-[18px] min-[821px]:col-span-6 min-[1181px]:col-span-4">
          <LinkedAlerts alerts={alerts} />
          <StudentSnapshot studentId={c.student} events={events ?? []} />
        </div>
      </div>
    </div>
  );
}
